import type { ProjectStore } from '@core/store';

import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import tinycolor from 'tinycolor2';

import { ProjectContext } from '@core/context';
import useMediaQuery from '@core/hooks/useMediaQuery';
import { useProjectStore } from '@core/store';
import classy from '@core/utils/classy';

export const ThemeContext = React.createContext<Partial<Theme>>({});
ThemeContext.displayName = 'ThemeContext';
export const SUPERHUB_GOOGLE_FONTS_LINK_ID = 'rm-superhub-google-fonts';

type ColorMode = ProjectStore['data']['appearance']['brand']['theme'];

interface Theme {
  'color-link-background'?: string;
  'color-link-button'?: string;
  'color-link-button-alpha-25'?: string;
  'color-link-button-alpha-50'?: string;
  'color-link-button-border'?: string;
  'color-link-button-darken-5'?: string;
  'color-link-button-darken-10'?: string;
  'color-link-button-text'?: string;
  'color-link-primary'?: string;
  'color-link-primary-alpha-25'?: string;
  'color-link-primary-alpha-50'?: string;
  'color-link-primary-dark-override'?: string;
  'color-link-primary-darken-5'?: string;
  'color-link-primary-darken-10'?: string;
  'color-link-primary-darken-20'?: string;
  'color-link-text'?: string;
  'color-login-link'?: string;
  'color-login-link-darken-10'?: string;
  'color-login-link-primary-alpha-50'?: string;
  'color-login-link-text'?: string;
  'color-primary'?: string;
  'color-primary-alpha-25'?: string;
  'color-primary-alt'?: string;
  'color-primary-darken-10'?: string;
  'color-primary-darken-20'?: string;
  'color-primary-inverse'?: string;
  'color-tab-active'?: string;
  colorMode: ColorMode;
  'rm-font-body'?: string;
  'rm-font-heading'?: string;
  'rm-font-mono'?: string;
  setColorMode: (mode: ColorMode) => void;
}

// Use a custom-defined `isLight` function over tinycolor's own so we
// have more control over the exact color threshold.
export const isLight = (color: tinycolor.Instance | string | undefined) => {
  return tinycolor(color).getLuminance() > 0.45;
};

// Check if a color is perceptibly darker than the default text color
export const isDark = (color: tinycolor.Instance | string | undefined) => {
  return tinycolor(color).getLuminance() < 0.02;
};

export const darken = (color: tinycolor.Instance | string | undefined) => {
  return tinycolor(color).darken(15).toHexString();
};

export const MIN_UI_CONTRAST = 2; // wcag 2.1 aa UI components
export const MIN_TEXT_CONTRAST = 3; // wcag 2.1 aa large text

// important: keep this css var named here if we ever need to find & replace:
// --color-bg-page colors
export const LIGHT_MODE_BG_PAGE = '#ffffff';
export const DARK_MODE_BG_PAGE = '#090c0e'; // gray5
const LIGHT_MODE_FALLBACK_COLOR = '#2b353b'; // gray20
const DARK_MODE_FALLBACK_COLOR = '#ffffff';

interface LinkColor {
  /* color scheme (light/dark) */
  colorMode: ColorMode;
  /* link color for dark mode */
  darkLinkColor: string | null;
  /* link color for light mode */
  linkColor: string | null;
  primaryColor: string;
}

const getLinkColor = ({ darkLinkColor, linkColor, primaryColor, colorMode }: LinkColor) => {
  if (colorMode === 'dark') {
    const color = darkLinkColor || primaryColor;
    return tinycolor.readability(color, DARK_MODE_BG_PAGE) < MIN_TEXT_CONTRAST ? DARK_MODE_FALLBACK_COLOR : color;
  }

  const color = linkColor || primaryColor;
  return tinycolor.readability(color, LIGHT_MODE_BG_PAGE) < MIN_TEXT_CONTRAST ? LIGHT_MODE_FALLBACK_COLOR : color;
};

/**
 * Builds a Google Fonts URL for the given font families with the weight
 * range used by the Hub (400, 500, 600), filtering out nullish values.
 */
const getGoogleFontsUrl = (...fonts: (string | null | undefined)[]) => {
  const families = fonts
    .filter(Boolean)
    .map(f => `family=${encodeURIComponent(f!).replace(/%20/g, '+')}:wght@400;500;600;700`);
  return families.length ? `https://fonts.googleapis.com/css2?${families.join('&')}&display=swap` : null;
};

const AppTheme = ({ children, theme: initialTheme = {} }: { children: React.ReactNode; theme?: Partial<Theme> }) => {
  const { project } = useContext(ProjectContext);
  const { appearance: { colors = {} } = {}, flags: { superHubTypography = false } = {} } = project;

  const [
    primaryColor,
    linkColor,
    darkLinkColor,
    headerType,
    gradientColor,
    colorScheme,
    projectLayout,
    headingFont,
    bodyFont,
    codeFont,
    spacing,
    customHeading,
    customBody,
    customCode,
  ] = useProjectStore(s => [
    // Fall back to the logo color if the primary color hasn't been set
    // Then fallback to the default blue if logo.main is null
    s.data.appearance.brand.primary_color || s.data.appearance.logo.main?.color || '#018ef5',
    s.data.appearance.brand.link_color,
    s.data.appearance.brand.link_color_dark,
    s.data.appearance.header.type,
    s.data.appearance.header.gradient_color,
    s.data.appearance.brand.theme,
    s.data.appearance.layout.style,
    s.data.appearance.typography?.heading_font,
    s.data.appearance.typography?.body_font,
    s.data.appearance.typography?.code_font,
    s.data.appearance.typography?.spacing,
    s.data.appearance.typography?.custom_heading,
    s.data.appearance.typography?.custom_body,
    s.data.appearance.typography?.custom_code,
  ]);

  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const [colorMode, setColorMode] = useState(colorScheme);
  const resolvedColorMode = colorMode === 'system' ? (prefersDarkMode ? 'dark' : 'light') : colorMode;

  const themeColors: Partial<Theme> = {
    'color-primary': primaryColor,
  };

  themeColors['color-primary-inverse'] = isLight(tinycolor(primaryColor)) ? '#384248' : '#fff';
  themeColors['color-primary-alt'] = gradientColor || tinycolor(primaryColor).darken(15).saturate(15).toHexString();
  themeColors['color-primary-darken-10'] = tinycolor(primaryColor || '#018ef5')
    .darken(10)
    .toString();
  themeColors['color-primary-darken-20'] = tinycolor(primaryColor || '#018ef5')
    .darken(20)
    .toString();
  themeColors['color-primary-alpha-25'] = tinycolor(primaryColor || '#018ef5')
    .setAlpha(0.25)
    .toRgbString();

  const linkColorArgs = { colorMode: resolvedColorMode, darkLinkColor, linkColor, primaryColor };
  const resolvedLinkColor = getLinkColor(linkColorArgs);
  const rawLinkColor = (resolvedColorMode === 'dark' ? darkLinkColor : linkColor) ?? primaryColor;

  themeColors['color-link-primary'] = resolvedLinkColor;
  themeColors['color-link-primary-dark-override'] = getLinkColor({
    colorMode: 'dark',
    darkLinkColor,
    linkColor,
    primaryColor,
  }); // For UI components that only have a dark mode and no light mode counterpart
  themeColors['color-link-button'] = rawLinkColor;
  const isLinkButtonLight = isLight(tinycolor(rawLinkColor));
  themeColors['color-link-button-text'] = isLinkButtonLight ? '#384248' : '#fff';
  themeColors['color-link-button-border'] = isLinkButtonLight ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.2)';
  themeColors['color-link-button-darken-5'] = tinycolor(rawLinkColor).darken(5).toString();
  themeColors['color-link-button-darken-10'] = tinycolor(rawLinkColor).darken(10).toString();
  themeColors['color-link-button-alpha-50'] = tinycolor(rawLinkColor).setAlpha(0.5).toRgbString();
  themeColors['color-link-button-alpha-25'] = tinycolor(rawLinkColor).setAlpha(0.25).toRgbString();
  themeColors['color-link-primary-darken-5'] = tinycolor(resolvedLinkColor).darken(5).toString();
  themeColors['color-link-primary-darken-10'] = tinycolor(resolvedLinkColor).darken(10).toString();
  themeColors['color-link-primary-darken-20'] = tinycolor(resolvedLinkColor).darken(20).toString();
  themeColors['color-link-primary-alpha-50'] = tinycolor(resolvedLinkColor).setAlpha(0.5).toRgbString();
  themeColors['color-link-primary-alpha-25'] = tinycolor(resolvedLinkColor).setAlpha(0.25).toRgbString();
  themeColors['color-link-background'] = tinycolor(resolvedLinkColor).setAlpha(0.09).toRgbString();
  themeColors['color-link-text'] =
    tinycolor.readability('#fff', resolvedLinkColor) >= tinycolor.readability('#384248', resolvedLinkColor)
      ? '#fff'
      : '#384248';

  const darkModeTabActive =
    resolvedColorMode === 'dark' && tinycolor.readability(primaryColor, DARK_MODE_BG_PAGE) < MIN_UI_CONTRAST
      ? DARK_MODE_FALLBACK_COLOR
      : primaryColor;
  const lightModeTabActive =
    resolvedColorMode === 'light' && tinycolor.readability(primaryColor, LIGHT_MODE_BG_PAGE) < MIN_UI_CONTRAST
      ? LIGHT_MODE_FALLBACK_COLOR
      : primaryColor;
  themeColors['color-tab-active'] = resolvedColorMode === 'dark' ? darkModeTabActive : lightModeTabActive;

  themeColors['color-login-link'] = colors.custom_login_link_color || themeColors['color-link-primary'] || '#018ef5';
  themeColors['color-login-link-text'] = isLight(themeColors['color-login-link']) ? '#384248' : '#fff';
  themeColors['color-login-link-darken-10'] = tinycolor(themeColors['color-login-link']).darken(10).toString();
  themeColors['color-login-link-primary-alpha-50'] = tinycolor(themeColors['color-login-link'])
    .setAlpha(0.5)
    .toRgbString();

  // Load Google Fonts dynamically when heading or body font changes.
  // Uses a ref to persist the <link> element across re-renders so that
  // in-progress font downloads are not cancelled when the URL changes.
  // Skip loading Google Fonts for slots that have custom fonts uploaded.
  const fontLinkRef = useRef<HTMLLinkElement | null>(null);
  const googleFontsUrl = superHubTypography
    ? getGoogleFontsUrl(
        customHeading?.url ? null : headingFont,
        customBody?.regular?.url ? null : bodyFont,
        customCode?.url ? null : codeFont,
      )
    : null;

  useEffect(() => {
    const existingLink = document.getElementById(SUPERHUB_GOOGLE_FONTS_LINK_ID) as HTMLLinkElement | null;

    // if user removes the typeface, remove the link
    if (!googleFontsUrl) {
      (fontLinkRef.current || existingLink)?.remove();
      fontLinkRef.current = null;
      return;
    }

    if (fontLinkRef.current || existingLink) {
      // Reuse the SSR preload link — flip rel to "stylesheet" so the already-fetched CSS is applied
      const link = (fontLinkRef.current || existingLink)!;
      link.id = SUPERHUB_GOOGLE_FONTS_LINK_ID;
      link.href = googleFontsUrl;
      link.rel = 'stylesheet';
      fontLinkRef.current = link;
    } else {
      const link = document.createElement('link');
      link.id = SUPERHUB_GOOGLE_FONTS_LINK_ID;
      link.rel = 'stylesheet';
      link.href = googleFontsUrl;
      document.head.appendChild(link);
      fontLinkRef.current = link;
    }
  }, [googleFontsUrl]);

  // prevents the <link> element from being orphaned if the
  // component unmounts while a font is still active.
  useEffect(() => {
    return () => {
      fontLinkRef.current?.remove();
    };
  }, []);

  // Inject @font-face rules for custom uploaded fonts
  const customFontStyleRef = useRef<HTMLStyleElement | null>(null);
  const customFontFaces = useMemo(() => {
    if (!superHubTypography) return null;

    const rules: string[] = [];

    if (customHeading?.url) {
      rules.push(`@font-face {
  font-family: 'CustomHeading';
  src: url('${customHeading.url}') format('${customHeading.format}');
  font-weight: 600;
  font-display: swap;
}`);
    }

    if (customBody?.regular?.url) {
      rules.push(`@font-face {
  font-family: 'CustomBody';
  src: url('${customBody.regular.url}') format('${customBody.regular.format}');
  font-weight: 400;
  font-display: swap;
}`);
    }
    if (customBody?.medium?.url) {
      rules.push(`@font-face {
  font-family: 'CustomBody';
  src: url('${customBody.medium.url}') format('${customBody.medium.format}');
  font-weight: 500;
  font-display: swap;
}`);
    }
    if (customBody?.semibold?.url) {
      rules.push(`@font-face {
  font-family: 'CustomBody';
  src: url('${customBody.semibold.url}') format('${customBody.semibold.format}');
  font-weight: 600;
  font-display: swap;
}`);
    }

    if (customCode?.url) {
      rules.push(`@font-face {
  font-family: 'CustomCode';
  src: url('${customCode.url}') format('${customCode.format}');
  font-weight: 400;
  font-display: swap;
}`);
    }

    return rules.length ? rules.join('\n') : null;
  }, [customHeading, customBody, customCode, superHubTypography]);

  useEffect(() => {
    if (!customFontFaces) {
      customFontStyleRef.current?.remove();
      customFontStyleRef.current = null;
      return;
    }

    if (customFontStyleRef.current) {
      customFontStyleRef.current.textContent = customFontFaces;
    } else {
      const style = document.createElement('style');
      style.textContent = customFontFaces;
      document.head.appendChild(style);
      customFontStyleRef.current = style;
    }
  }, [customFontFaces]);

  useEffect(() => {
    return () => {
      customFontStyleRef.current?.remove();
    };
  }, []);

  if (superHubTypography) {
    if (customHeading?.url) {
      themeColors['rm-font-heading'] = `'CustomHeading', var(--font-family-system)`;
    } else if (headingFont) {
      themeColors['rm-font-heading'] = `'${headingFont}', var(--font-family-system)`;
    }

    if (customBody?.regular?.url) {
      themeColors['rm-font-body'] = `'CustomBody', var(--font-family-system)`;
    } else if (bodyFont) {
      themeColors['rm-font-body'] = `'${bodyFont}', var(--font-family-system)`;
    }

    if (customCode?.url) {
      themeColors['rm-font-mono'] =
        `'CustomCode', "SF Mono", SFMono-Regular, ui-monospace, "DejaVu Sans Mono", Menlo, Consolas, monospace`;
    } else if (codeFont) {
      themeColors['rm-font-mono'] =
        `'${codeFont}', "SF Mono", SFMono-Regular, ui-monospace, "DejaVu Sans Mono", Menlo, Consolas, monospace`;
    }
  }

  const projectStyles = { ...initialTheme, ...themeColors };
  const projectTheme: Theme = { ...projectStyles, colorMode, setColorMode };

  return (
    <ThemeContext.Provider value={projectTheme}>
      <div
        className={classy(
          'App',
          'ThemeContext',
          `ThemeContext_${isLight(tinycolor(primaryColor)) ? 'light' : 'dark'}`,
          `ThemeContext_${projectLayout}`,
          superHubTypography && spacing && `ThemeContext_spacing_${spacing}`,
          headerType === 'line' && 'ThemeContext_line',
        )}
        style={Object.keys(projectStyles).reduce((obj, key) => {
          const val = projectStyles[key];
          obj[`--${key}`] = val;
          return obj;
        }, {})}
      >
        {children}
      </div>
    </ThemeContext.Provider>
  );
};

export default AppTheme;
